import { Logo } from "./Logo";
import { MapPin, Mail, MessageCircle, ArrowUpRight } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";

const NETWORK_APPS = [
  { label: "Galano Risk System", href: "https://ratingonline.store" },
  { label: "Business Plan", href: "https://businessplan.ratingonline.store" },
  { label: "Due Diligence Hub", href: "https://certytrace.store" },
  { label: "Due Diligence", href: "https://reputazione.shop" },
  { label: "Rexx Calling", href: "https://rexxcalling.com" },
  { label: "Empathy Sync", href: "https://rexxcalling.shop" },
  { label: "Nexxaris", href: "https://nexxaris.com" },
  { label: "Rexxind", href: "https://rexxind-preview.emergentagent.com" },
  { label: "Diritto Smart", href: "https://diritto-smart.emergentagent.com" },
  { label: "Counsel AI", href: "https://counsel-ai-platform-1.emergentagent.com" },
];

const slug = (s) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export const Footer = () => {
  const { t } = useI18n();
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#05101f] border-t border-white/10 pt-20 pb-10 px-6 overflow-hidden"
    >
      <div className="absolute inset-0 grid-lines opacity-20" />
      <div className="relative max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Prominent Rexxpons Network brand */}
          <div className="lg:col-span-6">
            <a
              href="https://www.rexxxponsnetwork.online"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="rexxpons-network-brand"
              className="group inline-flex items-center gap-3"
            >
              <span className="relative flex items-center justify-center w-11 h-11 rounded-xl border border-[#4BE3FF]/40 bg-[#0A1A2F] group-hover:cyan-glow transition-all duration-500">
                <span className="w-3 h-3 rounded-full bg-gradient-to-br from-[#4BE3FF] to-[#1E6FFF]" />
              </span>
              <span className="font-display font-extrabold text-3xl md:text-4xl tracking-tight text-white leading-none">
                Re<span className="text-[#1E6FFF]">xx</span>pons{" "}
                <span className="text-[#4BE3FF]">Network</span>
                <ArrowUpRight className="inline-block w-5 h-5 ml-1 -translate-y-1 text-[#A7B4C2] group-hover:text-[#4BE3FF] group-hover:translate-x-0.5 transition-all duration-300" />
              </span>
            </a>
            <p className="font-ui text-[15px] text-[#A7B4C2] mt-6 max-w-sm leading-relaxed">
              {t("footer.networkTagline")}
            </p>
            <div className="mt-6 flex items-center gap-2">
              <Logo size={26} />
            </div>
          </div>

          {/* Galano & Partners Law Firm */}
          <div className="lg:col-span-6">
            <div className="glass rounded-2xl p-7 h-full">
              <p className="font-mono text-[11px] uppercase tracking-widest text-[#4BE3FF] mb-4">
                Galano &amp; Partners Law Firm
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-[#4BE3FF] mt-0.5 shrink-0" />
                  <span className="font-ui text-[14px] text-[#F5F7FA]/90">
                    Piazza Malta 21, Salerno, Italy
                  </span>
                </li>
                <li>
                  <a
                    href="mailto:avv.luigigalano@pec.it"
                    data-testid="law-email"
                    className="group flex items-center gap-3 font-ui text-[14px] text-[#A7B4C2] hover:text-[#4BE3FF] transition-colors duration-300"
                  >
                    <Mail className="w-4 h-4 text-[#4BE3FF] shrink-0" />
                    avv.luigigalano@pec.it
                  </a>
                </li>
                <li>
                  <a
                    href="https://wa.me/393409592059"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="law-whatsapp"
                    className="group flex items-center gap-3 font-ui text-[14px] text-[#A7B4C2] hover:text-[#4BE3FF] transition-colors duration-300"
                  >
                    <MessageCircle className="w-4 h-4 text-[#4BE3FF] shrink-0" />
                    WhatsApp +39 340 959 2059
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-10 border-t border-white/10">
          <p className="font-mono text-[12px] uppercase tracking-widest text-[#4BE3FF] mb-6">
            {t("footer.appsTitle")}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {NETWORK_APPS.map((a) => (
              <a
                key={a.label}
                href={a.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`network-app-${slug(a.label)}`}
                className="group flex items-center justify-between rounded-xl border border-white/10 bg-[#0A1A2F]/60 px-4 py-3 hover:border-[#4BE3FF]/40 hover:bg-[#1E6FFF]/[0.06] transition-colors duration-300"
              >
                <span className="flex items-center gap-2.5 font-ui text-[14px] text-[#F5F7FA]/90 group-hover:text-white">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1E6FFF] group-hover:bg-[#4BE3FF] transition-colors" />
                  {a.label}
                </span>
                <ArrowUpRight className="w-4 h-4 text-[#A7B4C2]/60 group-hover:text-[#4BE3FF] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" />
              </a>
            ))}
          </div>
        </div>

        <div className="mt-10 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-mono text-[12px] text-[#A7B4C2]/70" data-testid="footer-copyright">
            © 2026 Rexxpons Network by L.G.
          </p>
          <div className="flex items-center gap-6 font-mono text-[12px] text-[#A7B4C2]/60">
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#4BE3FF] animate-pulse" />
              {t("footer.status")}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};
